import { StoreBadge } from '@/components';
import { SectionTitle } from '@/components/SectionTitle/SectionTitle.styles';
import { FALLBACK_PMT_LANGUAGE_CODE } from '@/constants';
import { useGeo, useGlobalState, useStatsigLayer } from '@/hooks';
import { useMyParcelsContext } from '@/modules/pmt/context';
import { useStatsigClient } from '@statsig/react-bindings';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import {
  AppBadgeSectionContainer,
  AppImageContainer,
  AppInfo,
  AppShowcase,
  AppTextContainer,
  DownloadButtons,
  MainTitle,
  SectionContainer,
  SubText,
} from './AppBadgeSection.styles';

const AppBadgeSection = () => {
  const { getProperImageUrl, pmtCurrentLanguageCode } = useGlobalState();
  const { mainTranslation } = useMyParcelsContext();
  const { geoInfo } = useGeo();
  const pathname = usePathname();
  const { logEvent } = useStatsigClient();
  const layer = useStatsigLayer('track_my_parcel_copy_layer');

  const {
    appBadgeSectionHeader,
    appBadgeMainText,
    appBadgeSubText,
    appBadgeImage,
    buttonLinkIos,
    buttonLinkAndroid,
    country,
  } = mainTranslation;

  // TODO: Apply multi language
  const isEnglish = pmtCurrentLanguageCode === FALLBACK_PMT_LANGUAGE_CODE;

  const headline = isEnglish ? layer.get('app_download_headline', appBadgeMainText) : appBadgeMainText;
  const body = isEnglish ? layer.get('app_download_body', appBadgeSubText) : appBadgeSubText;

  const shouldShowForCountry = !!country?.length && country.includes(geoInfo.country);
  if (!shouldShowForCountry) return null;

  if (!appBadgeSectionHeader && !appBadgeMainText && !appBadgeSubText) return null;

  const handleBadgeClick = (platform: 'app_store' | 'google_play') => {
    logEvent('app_badge_clicked', undefined, { platform, page: pathname });
  };

  return (
    <AppBadgeSectionContainer hasPadding={false} hasBorderTop={true}>
      <SectionContainer>
        {appBadgeSectionHeader && <SectionTitle>{appBadgeSectionHeader}</SectionTitle>}

        <AppShowcase>
          <AppInfo>
            {appBadgeImage?.url && (
              <AppImageContainer>
                <Image
                  src={getProperImageUrl(appBadgeImage)}
                  alt='Parcel Monitor App'
                  fill
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </AppImageContainer>
            )}

            <AppTextContainer>
              {headline && <MainTitle>{headline}</MainTitle>}
              {body && <SubText>{body}</SubText>}
            </AppTextContainer>

            <DownloadButtons>
              {buttonLinkIos && (
                <StoreBadge
                  platform='apple'
                  url={buttonLinkIos}
                  languageCode={pmtCurrentLanguageCode}
                  height={45}
                  onClick={() => handleBadgeClick('app_store')}
                  data-testid='app-store-badge'
                />
              )}

              {buttonLinkAndroid && (
                <StoreBadge
                  platform='google'
                  url={buttonLinkAndroid}
                  languageCode={pmtCurrentLanguageCode}
                  height={45}
                  onClick={() => handleBadgeClick('google_play')}
                  data-testid='google-play-badge'
                />
              )}
            </DownloadButtons>
          </AppInfo>
        </AppShowcase>
      </SectionContainer>
    </AppBadgeSectionContainer>
  );
};

export default AppBadgeSection;
